import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Icon,
  Loader,
  Message,
  Modal,
  Label,
  Dropdown,
} from 'semantic-ui-react';
import { API, isErrorShown, showError, showSuccess } from '../../helpers';
import i18n from '../../i18n';
import {
  StudioPage,
  StudioHero,
  StudioStat,
} from '../../components/StudioPage';

// 平台显示名映射（卡片副标题）：无映射的平台原样显示
const platformLabels = {
  workbuddy: 'WorkBuddy',
  opencode: 'OpenCode Zen',
  zen: 'OpenCode Zen',
  trae: 'Trae',
  catpaw: 'CatPaw',
  codex: 'Codex',
  openai: 'Codex',
};

// NoteEditor 行内备注编辑：铅笔进入编辑，回车/失焦保存，Esc 取消。
function NoteEditor({ accId, value, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  const save = async () => {
    setEditing(false);
    if (draft === value) return;
    setSaving(true);
    try {
      await API.post(`/api/pool/accounts/${accId}/note`, { note: draft });
      onSaved(draft);
    } catch (e) {
      // 保存失败静默（下次拉取会还原）；生产中可 toast
    } finally {
      setSaving(false);
    }
  };
  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') {
            setDraft(value);
            setEditing(false);
          }
        }}
        onBlur={save}
        placeholder='备注：国内 / 主力号…'
        style={{
          width: '100%',
          maxWidth: 420,
          fontSize: 12,
          padding: '6px 10px',
          borderRadius: 8,
          border: '1px solid rgba(40,125,120,0.35)',
          outline: 'none',
          background: '#fff',
        }}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      title='点击编辑备注'
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
        fontSize: 12,
        color: value ? '#3f3f46' : '#a1a1aa',
        background: value ? 'rgba(40,125,120,0.07)' : 'transparent',
        border: '1px dashed rgba(24,24,27,0.12)',
        borderRadius: 8,
        padding: '4px 10px',
        marginTop: 8,
      }}
    >
      <Icon
        name={saving ? 'spinner' : 'pencil'}
        loading={saving}
        size='small'
      />
      {value || '添加备注…'}
    </div>
  );
}

// PriorityEditor 行内优先级编辑：与 NoteEditor 同构。数字越大越先被调度，
// 高优先级账号限流/挂掉后自动降级到低优先级（PoolSelectActive 按 priority 降序）。
function PriorityEditor({ accId, value, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(String(value ?? 0));
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (!editing) setDraft(String(value ?? 0));
  }, [value, editing]);
  const save = async () => {
    setEditing(false);
    const n = parseInt(draft, 10);
    if (Number.isNaN(n) || n === value) return;
    setSaving(true);
    try {
      await API.post(`/api/pool/accounts/${accId}/priority`, { priority: n });
      onSaved(n);
      showSuccess(`优先级已更新为 ${n}`);
    } catch (e) {
      // 保存失败静默（下次拉取会还原）
    } finally {
      setSaving(false);
    }
  };
  if (editing) {
    return (
      <input
        autoFocus
        type='number'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') {
            setDraft(String(value ?? 0));
            setEditing(false);
          }
        }}
        onBlur={save}
        placeholder='0'
        style={{
          width: 90,
          fontSize: 12,
          padding: '6px 10px',
          borderRadius: 8,
          border: '1px solid rgba(40,125,120,0.35)',
          outline: 'none',
          background: '#fff',
        }}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      title='点击编辑调度优先级（数字越大越优先；高优先级挂了自动降级到低优先级）'
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        cursor: 'pointer',
        fontSize: 12,
        color: '#3f3f46',
        background: (value ?? 0) !== 0 ? 'rgba(40,125,120,0.07)' : 'transparent',
        border: '1px dashed rgba(24,24,27,0.12)',
        borderRadius: 8,
        padding: '4px 10px',
        marginTop: 8,
        marginLeft: 8,
      }}
    >
      <Icon
        name={saving ? 'spinner' : 'sort numeric descending'}
        loading={saving}
        size='small'
      />
      优先级 {value ?? 0}
    </div>
  );
}

const statusColors = {
  active: {
    border: 'rgba(40,125,120,0.22)',
    bg: 'rgba(240,253,250,0.88)',
    text: '#287d78',
    label: 'green',
  },
  expired: {
    border: 'rgba(190,65,65,0.18)',
    bg: 'rgba(254,242,242,0.88)',
    text: '#b83d3d',
    label: 'red',
  },
  rate_limited: {
    border: 'rgba(200,140,30,0.18)',
    bg: 'rgba(255,248,235,0.88)',
    text: '#c8821e',
    label: 'orange',
  },
  error: {
    border: 'rgba(190,65,65,0.18)',
    bg: 'rgba(254,242,242,0.88)',
    text: '#b83d3d',
    label: 'red',
  },
};

// 时间按当前界面语言格式化（原先硬编码 zh-CN，英文界面会得到中文式日期）
const dateLocale = () =>
  (i18n.resolvedLanguage || i18n.language || 'zh').startsWith('en')
    ? 'en-US'
    : 'zh-CN';

const formatTime = (t) => {
  if (!t || t === '0001-01-01T00:00:00Z' || t.startsWith('1970')) return '--';
  return new Date(t).toLocaleString(dateLocale(), {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// workbuddy 字段 last_checkin_at 为 unix 秒（int64），单独格式化
const formatUnix = (t) => {
  if (!t || t <= 0) return '--';
  return new Date(t * 1000).toLocaleString(dateLocale(), {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatReset = (seconds) => {
  if (!seconds || seconds <= 0) return '';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// 获取授权弹窗内的小控件样式（semantic-ui 之外的原生 input）
const ocLabelStyle = {
  display: 'block',
  fontSize: 11,
  color: '#71717a',
  marginBottom: 4,
};
const ocInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '7px 10px',
  borderRadius: 8,
  border: '1px solid rgba(24,24,27,0.12)',
  fontSize: 12,
  background: 'rgba(255,255,255,0.8)',
  outline: 'none',
};
const authPreviewStyle = {
  maxHeight: 200,
  overflow: 'auto',
  margin: 0,
  padding: '10px 12px',
  borderRadius: 10,
  background: 'rgba(24,24,27,0.04)',
  fontSize: 11,
  lineHeight: 1.6,
  fontFamily: 'monospace',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
};

// 带动效的自定义进度条
const AnimatedBar = ({ percent, label, resetText, color }) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setWidth(Math.min(100, percent)), 100);
    return () => clearTimeout(timer);
  }, [percent]);
  const barColor =
    percent > 80 ? '#ef4444' : percent > 50 ? '#f59e0b' : color || '#22c55e';
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 4,
        }}
      >
        <span style={{ fontSize: 11, color: '#71717a', fontWeight: 500 }}>
          {label}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: barColor }}>
          {percent.toFixed(1)}%
          {resetText && (
            <span style={{ fontWeight: 400, color: '#a1a1aa', marginLeft: 6 }}>
              {resetText}
            </span>
          )}
        </span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 999,
          background: 'rgba(24,24,27,0.06)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${width}%`,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${barColor}dd, ${barColor})`,
            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: `0 0 8px ${barColor}55`,
          }}
        />
      </div>
    </div>
  );
};

const Pool = () => {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [status, setStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState({});
  const [proxyNodes, setProxyNodes] = useState([]);
  const [proxyLoading, setProxyLoading] = useState({});
  const [checkingIn, setCheckingIn] = useState(false);
  const [traeCheckingIn, setTraeCheckingIn] = useState(false);
  // ── 获取授权弹窗（workbuddy 登录流 / opencode 文件流）──
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('workbuddy'); // workbuddy | opencode
  const [authRealm, setAuthRealm] = useState('global'); // global | cn
  const [authStatus, setAuthStatus] = useState('idle'); // idle|starting|waiting|done|expired|error
  const [authState, setAuthState] = useState(null); // {state, auth_url}
  const [authCred, setAuthCred] = useState(null); // 生成的授权文件内容
  const [authFilename, setAuthFilename] = useState('');
  const [authError, setAuthError] = useState('');
  const [authCopied, setAuthCopied] = useState(false);
  const [authImportMsg, setAuthImportMsg] = useState(null);
  const [exporting, setExporting] = useState(null);
  // 单账号签到：accChecking=进行中的账号 id；accCheckinMsg=最近结果 {id, ok, text}
  const [accChecking, setAccChecking] = useState(null);
  const [accCheckinMsg, setAccCheckinMsg] = useState(null);
  // 暂停/恢复：操作中的账号 id（按钮 loading 用）
  const [pausing, setPausing] = useState(null);
  // 平台筛选：'' = 全部；一键测试批量状态
  const [platformFilter, setPlatformFilter] = useState('');
  const [bulkTesting, setBulkTesting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const bulkAbortRef = React.useRef(false);

  // 可拉取模型：{[accountId]: {loading, models}} — 打开卡片模型区时拉取
  const [accModels, setAccModels] = useState({});
  // 展开模型清单的账号 id 集合
  const [modelsOpen, setModelsOpen] = useState({});
  // 列表交互：展开详情的账号（单开手风琴，'' = 全收起）
  const [expandedId, setExpandedId] = useState('');
  const [checkinLogsOpen, setCheckinLogsOpen] = useState(false);
  const [checkinLogs, setCheckinLogs] = useState({
    logs: [],
    today: null,
    total: 0,
  });
  const [ocForm, setOcForm] = useState({
    name: 'zen-官方直连',
    endpoint: '',
    concurrency: 2,
    priority: 0,
    window_limit: 5,
  });
  // 签到按钮只对有 WorkBuddy 账号的池有意义（其余账号会被签到轮直接跳过）
  const hasWorkbuddy = accounts.some((a) => a.platform === 'workbuddy');
  // Trae 签到按钮：有 CN 区 trae 账号才展示（SG 美元计费无签到，后端会跳过）
  const hasTraeCn = accounts.some(
    (a) =>
      a.platform === 'trae' &&
      !['sg', 'global'].includes((a.realm || '').toLowerCase())
  );
  // 平台筛选：从当前账号动态聚合平台清单（有啥平台显示啥，不写死）
  const platformOptions = React.useMemo(() => {
    const set = new Set(
      accounts.map((a) => (a.platform || '').trim()).filter(Boolean)
    );
    return [
      { key: '', text: t('pool.filter_all'), value: '' },
      ...[...set].sort().map((p) => ({ key: p, text: p, value: p })),
    ];
  }, [accounts, t]);
  // 按平台过滤后的卡片列表（大小写不敏感）
  const filteredAccounts = React.useMemo(
    () =>
      platformFilter
        ? accounts.filter(
            (a) =>
              (a.platform || '').toLowerCase() === platformFilter.toLowerCase()
          )
        : accounts,
    [accounts, platformFilter]
  );

  // 按平台分组的展示顺序（固定组序：workbuddy/trae/qoder/catpaw/opencode/zen/其他）
  const groupedAccounts = React.useMemo(() => {
    const order = ['workbuddy', 'trae', 'qoder', 'catpaw', 'opencode', 'zen'];
    const groups = new Map();
    for (const acc of filteredAccounts) {
      const p = (acc.platform || 'other').toLowerCase();
      if (!groups.has(p)) groups.set(p, []);
      groups.get(p).push(acc);
    }
    const sorted = [...groups.entries()].sort((a, b) => {
      const ia = order.indexOf(a[0]);
      const ib = order.indexOf(b[0]);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
    return sorted;
  }, [filteredAccounts]);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [listRes, statRes, nodesRes] = await Promise.all([
        API.get('/api/pool/accounts'),
        API.get('/api/pool/status'),
        API.get('/api/pool/proxy-nodes').catch(() => ({
          data: { subscriptions: [] },
        })),
      ]);
      if (listRes.data) setAccounts(listRes.data.accounts || []);
      if (statRes.data) setStatus(statRes.data);
      if (nodesRes.data) {
        const nodesData = nodesRes.data;
        setProxyNodes(
          Array.isArray(nodesData) ? nodesData : nodesData.subscriptions || []
        );
      }
    } catch (e) {
      if (!silent) showError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // 进入页面获取一次，之后每 3 秒静默刷新（不显示 loading）
  // 修复：轮询原先是 refresh 的复制品（少拉 proxy-nodes），两处易漂移；
  // 现在 refresh(silent) 参数化后统一走同一实现。
  useEffect(() => {
    refresh();
    const timer = setInterval(() => refresh(true), 3000);
    return () => clearInterval(timer);
  }, [refresh]);

  const handleImport = async (file) => {
    let data;
    try {
      const text = await file.text();
      data = JSON.parse(text);
    } catch (e) {
      setImportResult({
        success: false,
        error: t('pool.import_invalid_json', { msg: e.message }),
      });
      return;
    }
    if (data === undefined || data === null || typeof data !== 'object') {
      // 修复：JSON.parse("null")/数字等合法JSON但不是账号对象，直接发给后端
      // 会得到含糊的报错，前端先拦一道
      setImportResult({
        success: false,
        error: t('pool.import_invalid_json', { msg: 'not an object' }),
      });
      return;
    }
    try {
      const res = await API.post('/api/pool/import', data);
      if (res.data?.success) {
        setImportResult({ success: true, count: res.data.count });
        refresh();
      } else {
        setImportResult({
          success: false,
          error: res.data?.error || t('pool.import_failed'),
        });
      }
    } catch (e) {
      setImportResult({
        success: false,
        error: e.response?.data?.error || e.message,
      });
    }
  };

  // 手动触发 Trae 签到（09/21 双场之外的即时补跑）
  const handleTraeCheckin = async () => {
    setTraeCheckingIn(true);
    try {
      await API.post('/api/pool/trae/checkin');
      showSuccess(t('pool.checkin_triggered'));
      await refresh(true);
    } catch (e) {
      if (e.response?.status === 409) {
        showError(t('pool.checkin_running'));
      } else if (!isErrorShown(e)) {
        showError(e.message);
      }
    } finally {
      setTraeCheckingIn(false);
    }
  };

  // 手动触发 WorkBuddy 签到（09/21 双场之外的即时补跑）
  const handleCheckin = async () => {
    setCheckingIn(true);
    try {
      await API.post('/api/pool/workbuddy/checkin');
      showSuccess(t('pool.checkin_triggered'));
      await refresh(true);
    } catch (e) {
      // 409=已有签到任务在运行（后端就绪即返回该码）。
      // 其余错误 helpers/api 的拦截器已经弹过一次提示，这里必须用
      // isErrorShown 去重，否则同一错误会连弹两条，且第二条是后端返回的
      // 中文原文（绕过 i18n，英文界面下显示中文）。
      if (e.response?.status === 409) {
        showError(t('pool.checkin_running'));
      } else if (!isErrorShown(e)) {
        showError(e.message);
      }
    } finally {
      setCheckingIn(false);
    }
  };

  // 单账号签到（wb 信息卡按钮）：同步等结果，展示「+N 积分」；记录落 pool_checkin_logs。
  const handleAccountCheckin = async (acc) => {
    setAccChecking(acc.id);
    setAccCheckinMsg(null);
    try {
      const res = await API.post(`/api/pool/accounts/${acc.id}/checkin`);
      const r = (res.data && res.data.result) || {};
      let text;
      if (r.status === 'checked') {
        text =
          r.delta != null
            ? t('pool.checkin_success_delta', { delta: r.delta })
            : t('pool.checkin_success');
      } else if (r.status === 'already') {
        text = t('pool.checkin_already');
      } else {
        text = r.message
          ? t('pool.checkin_failed') + ': ' + r.message
          : t('pool.checkin_failed');
      }
      setAccCheckinMsg({ id: acc.id, ok: r.status !== 'failed', text });
      refresh(true);
    } catch (e) {
      if (!isErrorShown(e)) showError(e.message);
      setAccCheckinMsg({
        id: acc.id,
        ok: false,
        text: t('pool.checkin_failed'),
      });
    } finally {
      setAccChecking(null);
    }
  };

  // 签到记录弹窗：打开时拉一次最近 100 条 + 今日汇总。
  const openCheckinLogs = async () => {
    setCheckinLogsOpen(true);
    try {
      const res = await API.get('/api/pool/checkin/logs?limit=100');
      setCheckinLogs(res.data || { logs: [], today: null, total: 0 });
    } catch (e) {
      if (!isErrorShown(e)) showError(e.message);
    }
  };

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const res = await API.post(`/api/pool/test/${id}`);
      setTestResult((prev) => ({ ...prev, [id]: res.data }));
    } catch (e) {
      setTestResult((prev) => ({
        ...prev,
        [id]: { success: false, error: e.message },
      }));
    } finally {
      setTesting(null);
    }
  };

  // 一键测试：对（按当前筛选后的）全部账号并发 3 路逐个触发单账号测试。
  // 复用 handleTest 的后端接口与卡片结果展示；可随时点「停止」中断剩余队列。
  const handleTestAll = async () => {
    if (bulkTesting) {
      bulkAbortRef.current = true;
      return;
    }
    const targets = filteredAccounts;
    if (!targets.length) return;
    if (!window.confirm(t('pool.test_all_confirm', { count: targets.length })))
      return;
    bulkAbortRef.current = false;
    setBulkTesting(true);
    setBulkProgress({ done: 0, total: targets.length });
    const CONCURRENCY = 3;
    let cursor = 0;
    let done = 0;
    const worker = async () => {
      while (!bulkAbortRef.current) {
        const idx = cursor;
        if (idx >= targets.length) return;
        cursor += 1;
        const acc = targets[idx];
        // 与 handleTest 相同的请求/结果落点，但不动全局 testing 态
        try {
          const res = await API.post(`/api/pool/test/${acc.id}`);
          setTestResult((prev) => ({ ...prev, [acc.id]: res.data }));
        } catch (e) {
          setTestResult((prev) => ({
            ...prev,
            [acc.id]: { success: false, error: e.message },
          }));
        }
        done += 1;
        setBulkProgress({ done, total: targets.length });
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, targets.length) }, worker)
    );
    setBulkTesting(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('pool.confirm_delete'))) return;
    try {
      await API.delete(`/api/pool/accounts/${id}`);
      showSuccess(t('pool.deleted'));
      // 静默刷新：非静默会把整张列表换成居中 Loader，操作后闪一下很难受
      refresh(true);
    } catch (e) {
      showError(e.message);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(t('pool.confirm_clear'))) return;
    try {
      await API.delete('/api/pool/accounts');
      showSuccess(t('pool.cleared'));
      refresh(true);
    } catch (e) {
      showError(e.message);
    }
  };

  // 暂停/恢复账号：暂停后不参与任何平台调度（模型与转发途径均不可用）
  const handleTogglePause = async (acc) => {
    const next = !acc.paused;
    if (next && !window.confirm(t('pool.pause_confirm'))) return;
    if (!next && !window.confirm(t('pool.resume_confirm'))) return;
    setPausing(acc.id);
    try {
      await API.post(`/api/pool/accounts/${acc.id}/pause`, { paused: next });
      showSuccess(next ? t('pool.paused_success') : t('pool.resumed_success'));
      refresh(true);
    } catch (e) {
      if (!isErrorShown(e)) showError(e.message);
    } finally {
      setPausing(null);
    }
  };

  // 展开/收起「可用模型」区；首次展开时拉取该账号的模型清单
  const toggleModels = async (acc) => {
    const open = !modelsOpen[acc.id];
    setModelsOpen((prev) => ({ ...prev, [acc.id]: open }));
    if (open && !accModels[acc.id]) {
      setAccModels((prev) => ({
        ...prev,
        [acc.id]: { loading: true, models: [] },
      }));
      try {
        const res = await API.get(`/api/pool/accounts/${acc.id}/models`);
        setAccModels((prev) => ({
          ...prev,
          [acc.id]: { loading: false, models: res.data?.models || [] },
        }));
      } catch (e) {
        setAccModels((prev) => ({
          ...prev,
          [acc.id]: { loading: false, models: [], error: e.message },
        }));
      }
    }
  };

  // ── 获取授权：workbuddy 登录流（state→登录→token 轮询）──
  const resetAuthFlow = () => {
    setAuthStatus('idle');
    setAuthState(null);
    setAuthCred(null);
    setAuthFilename('');
    setAuthError('');
    setAuthCopied(false);
    setAuthImportMsg(null);
  };

  // ── 获取授权：Trae 静默登录流（state→登录→粘贴回调链接→直接建账号）──
  const [traeAuth, setTraeAuth] = useState({
    status: 'idle', // idle | starting | waiting | done | error
    state: null,
    authUrl: '',
    realm: 'cn',
    callbackUrl: '',
    error: '',
    result: null,
  });
  const resetTraeAuth = () =>
    setTraeAuth({
      status: 'idle',
      state: null,
      authUrl: '',
      realm: traeAuth.realm,
      callbackUrl: '',
      error: '',
      result: null,
    });

  // ── 获取授权：CatPaw 静默登录流（state→登录→服务端轮询 token→直接入池）──
  const [catpawAuth, setCatpawAuth] = useState({
    status: 'idle', // idle | starting | waiting | done | error
    state: null,
    authUrl: '',
    error: '',
    result: null,
  });
  const resetCatpawAuth = () =>
    setCatpawAuth({
      status: 'idle',
      state: null,
      authUrl: '',
      error: '',
      result: null,
    });
  const handleCatpawStart = async () => {
    setCatpawAuth((p) => ({ ...p, status: 'starting', error: '' }));
    try {
      const res = await API.post('/api/pool/auth/catpaw/start');
      const { success, message, error } = res.data;
      if (!success) {
        setCatpawAuth((p) => ({
          ...p,
          status: 'error',
          error: error || message,
        }));
        return;
      }
      setCatpawAuth((p) => ({
        ...p,
        status: 'waiting',
        state: res.data.state,
        authUrl: res.data.auth_url,
      }));
    } catch (e) {
      setCatpawAuth((p) => ({ ...p, status: 'error', error: e.message }));
    }
  };

  // CatPaw 登录轮询：每 3 秒查一次（服务端 poll-token 自动收 token，无需粘贴回调）。
  useEffect(() => {
    if (!authOpen || catpawAuth.status !== 'waiting' || !catpawAuth.state) {
      return undefined;
    }
    const timer = setInterval(async () => {
      try {
        const res = await API.get(
          `/api/pool/auth/catpaw/poll?state=${encodeURIComponent(
            catpawAuth.state
          )}`
        );
        const d = res.data || {};
        if (d.status === 'done') {
          setCatpawAuth((p) => ({
            ...p,
            status: 'done',
            result: d.result || null,
          }));
          showSuccess(t('pool.auth_done'));
          refresh(true);
        } else if (d.status === 'expired') {
          setCatpawAuth((p) => ({
            ...p,
            status: 'error',
            error: t('pool.auth_expired'),
          }));
        } else if (d.status === 'error' || d.success === false) {
          setCatpawAuth((p) => ({
            ...p,
            status: 'error',
            error: d.error || d.message || '',
          }));
        }
        // pending → 继续轮询
      } catch (e) {
        // 网络抖动忽略，下一轮重试
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [authOpen, catpawAuth.status, catpawAuth.state]);

  // ── Qoder：登录引导流（官方 device flow 凭据落 CLI 本地，页面只做引导+导入指引）──
  const [qoderAuth, setQoderAuth] = useState({
    status: 'idle', // idle | starting | ready
    loginUrl: '',
    guide: [],
    error: '',
  });
  const handleQoderStart = async () => {
    setQoderAuth((p) => ({ ...p, status: 'starting', error: '' }));
    try {
      const res = await API.post('/api/pool/auth/qoder/start');
      const { success, error } = res.data;
      if (!success) {
        setQoderAuth((p) => ({
          ...p,
          status: 'idle',
          error: error || '获取指引失败',
        }));
        return;
      }
      setQoderAuth((p) => ({
        ...p,
        status: 'ready',
        loginUrl: res.data.login_url,
        guide: res.data.guide || [],
      }));
    } catch (e) {
      setQoderAuth((p) => ({ ...p, status: 'idle', error: e.message }));
    }
  };

  const handleTraeStart = async () => {
    setTraeAuth((p) => ({ ...p, status: 'starting', error: '' }));
    try {
      const res = await API.post('/api/pool/auth/trae/start', {
        realm: traeAuth.realm,
      });
      const { success, message } = res.data;
      if (!success) {
        setTraeAuth((p) => ({ ...p, status: 'error', error: message }));
        return;
      }
      setTraeAuth((p) => ({
        ...p,
        status: 'waiting',
        state: res.data.state,
        authUrl: res.data.auth_url,
        realm: res.data.realm || p.realm,
      }));
    } catch (e) {
      setTraeAuth((p) => ({ ...p, status: 'error', error: e.message }));
    }
  };
  const handleTraeComplete = async () => {
    if (!traeAuth.callbackUrl.trim() || !traeAuth.state) return;
    setTraeAuth((p) => ({ ...p, status: 'starting', error: '' }));
    try {
      const res = await API.post('/api/pool/auth/trae/complete', {
        state: traeAuth.state,
        callback_url: traeAuth.callbackUrl.trim(),
      });
      const { success, message, error } = res.data;
      if (!success) {
        setTraeAuth((p) => ({
          ...p,
          status: 'error',
          error: error || message,
        }));
        return;
      }
      setTraeAuth((p) => ({ ...p, status: 'done', result: res.data }));
      showSuccess(t('pool.auth_done'));
      refresh(true);
    } catch (e) {
      setTraeAuth((p) => ({ ...p, status: 'error', error: e.message }));
    }
  };

  const closeAuthModal = () => {
    setAuthOpen(false);
    resetAuthFlow();
  };

  // 复制到剪贴板：http 非安全上下文下 navigator.clipboard 不可用，回退 execCommand
  const copyText = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      // 忽略，走回退
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch (e) {
      return false;
    }
  };

  const downloadJsonFile = (filename, obj) => {
    try {
      const blob = new Blob([JSON.stringify(obj, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'auth.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      showError(e.message);
    }
  };

  const handleAuthStart = async () => {
    setAuthStatus('starting');
    setAuthError('');
    setAuthImportMsg(null);
    try {
      const res = await API.post('/api/pool/auth/workbuddy/start', {
        realm: authRealm,
      });
      if (res.data?.success) {
        setAuthState({ state: res.data.state, auth_url: res.data.auth_url });
        setAuthStatus('waiting');
      } else {
        setAuthError(res.data?.error || t('pool.import_failed'));
        setAuthStatus('error');
      }
    } catch (e) {
      setAuthError(e.response?.data?.error || e.message);
      setAuthStatus('error');
    }
  };

  // 生成物（workbuddy 凭据 / opencode 实例文件）一键导入到池
  const handleAuthImport = async (payload) => {
    try {
      const res = await API.post('/api/pool/import', payload);
      if (res.data?.success) {
        setAuthImportMsg({ success: true, count: res.data.count });
        refresh(true);
      } else {
        setAuthImportMsg({
          success: false,
          error: res.data?.error || t('pool.import_failed'),
        });
      }
    } catch (e) {
      setAuthImportMsg({
        success: false,
        error: e.response?.data?.error || e.message,
      });
    }
  };

  // 账号卡片导出：拉取可直接再导入的 JSON 并触发浏览器下载
  const handleAccountExport = async (acc) => {
    setExporting(acc.id);
    try {
      const res = await API.get(`/api/pool/accounts/${acc.id}/export`);
      if (res.data?.success) {
        downloadJsonFile(res.data.filename, res.data.content);
        showSuccess(t('pool.exported'));
      } else {
        showError(res.data?.error || t('pool.export_failed'));
      }
    } catch (e) {
      if (!isErrorShown(e)) showError(e.message);
    } finally {
      setExporting(null);
    }
  };

  // opencode 文件流 payload（与账号无关，三件套头由反代端注入）
  const ocPayload = {
    platform: 'opencode',
    instances: [
      {
        name: ocForm.name.trim() || 'zen-官方直连',
        ...(ocForm.endpoint.trim() ? { endpoint: ocForm.endpoint.trim() } : {}),
        concurrency: Number(ocForm.concurrency) || 2,
        priority: Number(ocForm.priority) || 0,
        window_limit: Number(ocForm.window_limit) || 5,
      },
    ],
  };

  // 登录等待轮询：每 3 秒查一次，done 后自动拿到凭据；expired 停表提示重取
  useEffect(() => {
    if (!authOpen || authStatus !== 'waiting' || !authState?.state) {
      return undefined;
    }
    const timer = setInterval(async () => {
      try {
        const res = await API.get(
          `/api/pool/auth/workbuddy/poll?state=${encodeURIComponent(
            authState.state
          )}`
        );
        const d = res.data || {};
        if (d.status === 'done') {
          setAuthCred(d.credential || null);
          setAuthFilename(d.filename || '');
          setAuthStatus('done');
        } else if (d.status === 'expired') {
          setAuthStatus('expired');
        } else if (d.status === 'error' && d.success === false) {
          setAuthError(d.error || '');
          setAuthStatus('error');
        }
        // pending / 其他 → 继续轮询
      } catch (e) {
        // 网络抖动忽略，下一轮重试；服务端明确失效时 data 带 status
        const st = e.response?.data;
        if (st && (st.status === 'expired' || st.status === 'error')) {
          setAuthError(st.error || '');
          setAuthStatus(st.status === 'expired' ? 'expired' : 'error');
        }
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [authOpen, authStatus, authState]);

  // 代理节点选项 — 精简文本，用 Popup 显示完整信息
  const proxyOptions = [
    { key: 'direct', value: '0-0', text: t('pool.direct') },
  ];
  proxyNodes.forEach((sub) => {
    (sub.nodes || []).forEach((node) => {
      const flag = node.name?.match(/[\u{1F1E6}-\u{1F1FF}]{2}/u)?.[0] || '';
      const shortName =
        node.name
          ?.replace(/[\u{1F1E6}-\u{1F1FF}]{2}/u, '')
          .trim()
          .split(' ')[0] || node.host;
      const latencyStr =
        node.test_latency_ms > 0 ? `${node.test_latency_ms}ms` : '';
      const statusIcon =
        node.test_status === 'success'
          ? '✓'
          : node.test_status === 'failed'
          ? '✕'
          : '';
      proxyOptions.push({
        key: `${sub.id}-${node.id}`,
        value: `${sub.id}-${node.id}`,
        text: `${flag} ${shortName}${latencyStr ? ` ${latencyStr}` : ''}${
          statusIcon ? ` ${statusIcon}` : ''
        }`,
      });
    });
  });

  const handleProxyChange = async (accountId, value) => {
    setProxyLoading((prev) => ({ ...prev, [accountId]: true }));
    try {
      const [subId, nodeId] = value.split('-').map(Number);
      await API.post(`/api/pool/proxy-node/${accountId}`, {
        subscription_id: subId || 0,
        node_id: nodeId || 0,
      });
      showSuccess(t('pool.proxy_set'));
      // 静默刷新：非静默会卸载整张列表与刚操作的下拉框，
      // 选择会先"弹回"再整页闪一次 Loader。
      refresh(true);
    } catch (e) {
      showError(e.message);
    } finally {
      setProxyLoading((prev) => ({ ...prev, [accountId]: false }));
    }
  };

  return (
    <StudioPage className='studio-management-page'>
      <StudioHero
        icon='server'
        kicker='AI GATEWAY'
        title={t('header.pool')}
        description={t('pool.stats_desc')}
      >
        <Button primary size='small' onClick={() => setImportOpen(true)}>
          <Icon name='upload' /> {t('pool.import')}
        </Button>
        <Button size='small' color='violet' onClick={() => setAuthOpen(true)}>
          <Icon name='key' /> {t('pool.get_auth')}
        </Button>
        <Button size='small' onClick={refresh} loading={loading}>
          <Icon name='refresh' /> {t('pool.refresh')}
        </Button>
      </StudioHero>

      {/* Stats */}
      <div className='studio-management-overview'>
        <div>
          <span className='studio-section-eyebrow'>POOL OVERVIEW</span>
          <h2>{t('pool.account_stats')}</h2>
          <p>{t('pool.stats_desc')}</p>
        </div>
        <div className='studio-management-overview__stats pool-overview-stats'>
          <StudioStat
            icon='server'
            label={t('pool.total') || '总账号'}
            value={status.total || 0}
          />
          <StudioStat
            icon='check circle'
            label={t('pool.active') || '活跃'}
            value={status.active || 0}
            tone='success'
          />
          <StudioStat
            icon='hourglass half'
            label={t('pool.rate_limited') || '限流'}
            value={status.rate_limited || 0}
            tone='warning'
          />
          <StudioStat
            icon='times circle'
            label={t('pool.expired') || '过期'}
            value={status.expired || 0}
            tone='danger'
          />
        </div>
      </div>

      {/* Import Modal */}
      <Modal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportResult(null);
        }}
        size='small'
        closeIcon
      >
        <Modal.Header>{t('pool.import_title')}</Modal.Header>
        <Modal.Content>
          <input
            type='file'
            accept='.json'
            style={{ display: 'none' }}
            id='pool-file-input'
            onChange={(e) => {
              const f = e.target.files[0];
              if (f) {
                if (!f.name.toLowerCase().endsWith('.json')) {
                  showError(t('pool.import_json_only'));
                } else {
                  handleImport(f);
                }
              }
              e.target.value = ''; // 修复：同一文件第二次选择不触发 onChange
            }}
          />
          <div
            onClick={() => document.getElementById('pool-file-input').click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.dataset.drag = '1';
              e.currentTarget.style.borderColor = '#4f63a8';
              e.currentTarget.style.background = 'rgba(79,99,168,0.06)';
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = 'rgba(24,24,27,0.12)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.6)';
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = 'rgba(24,24,27,0.12)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.6)';
              const file = e.dataTransfer?.files?.[0];
              if (file) {
                if (!file.name.toLowerCase().endsWith('.json')) {
                  showError(t('pool.import_json_only'));
                  return;
                }
                handleImport(file);
              }
            }}
            style={{
              cursor: 'pointer',
              textAlign: 'center',
              padding: '36px 20px',
              border: '2px dashed rgba(24,24,27,0.12)',
              borderRadius: '14px',
              background: 'rgba(255,255,255,0.6)',
              transition: 'all .2s',
            }}
          >
            <Icon
              name='cloud upload'
              size='huge'
              style={{ color: '#a1a1aa', marginBottom: 12 }}
            />
            <p style={{ color: '#52525b', fontSize: 14, margin: '8px 0 4px' }}>
              {t('pool.import_hint')}
            </p>
            <p style={{ color: '#a1a1aa', fontSize: 12 }}>
              {t('pool.import_format')}
            </p>
            <p style={{ color: '#a1a1aa', fontSize: 11, marginTop: 6 }}>
              {t('pool.import_drag_hint')}
            </p>
          </div>
          {importResult && (
            <Message
              positive={importResult.success}
              negative={!importResult.success}
              style={{ marginTop: 16 }}
            >
              <Message.Header>
                {importResult.success
                  ? t('pool.import_success')
                  : t('pool.import_failed')}
              </Message.Header>
              <p>
                {importResult.success
                  ? t('pool.import_success_count', {
                      count: importResult.count,
                    })
                  : importResult.error}
              </p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              setImportOpen(false);
              setImportResult(null);
            }}
          >
            {t('pool.close')}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Check-in Records Modal — 签到流水（最近 100 条）+ 今日汇总 */}
      <Modal
        open={checkinLogsOpen}
        onClose={() => setCheckinLogsOpen(false)}
        size='small'
        closeIcon
      >
        <Modal.Header>{t('pool.checkin_records_title')}</Modal.Header>
        <Modal.Content>
          {checkinLogs.today ? (
            <div style={{ marginBottom: 12, fontSize: 12, color: '#52525b' }}>
              <b>{t('pool.checkin_today')}</b>
              {' · '}
              {t('pool.checkin_today_summary', {
                checked: checkinLogs.today.checked || 0,
                already: checkinLogs.today.already || 0,
                failed: checkinLogs.today.failed || 0,
                added: checkinLogs.today.added || 0,
              })}
            </div>
          ) : null}
          <div
            style={{
              maxHeight: '52vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {/* 列头 — 引导五列含义（时间/账号/状态/积分/来源） */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '0 10px 2px',
                fontSize: 10,
                color: '#a1a1aa',
                letterSpacing: '0.05em',
              }}
            >
              <span style={{ minWidth: 78 }}>{t('pool.checkin_col_time')}</span>
              <span style={{ flex: 1 }}>{t('pool.checkin_col_account')}</span>
              <span style={{ minWidth: 36, textAlign: 'center' }}>
                {t('pool.checkin_col_status')}
              </span>
              <span style={{ minWidth: 44, textAlign: 'right' }}>
                {t('pool.checkin_col_delta')}
              </span>
              <span style={{ minWidth: 50, textAlign: 'right' }}>
                {t('pool.checkin_col_mode')}
              </span>
            </div>
            {checkinLogs.logs && checkinLogs.logs.length > 0 ? (
              checkinLogs.logs.map((l) => (
                <div
                  key={l.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 10px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(24,24,27,0.05)',
                    fontSize: 11,
                  }}
                >
                  <span
                    style={{
                      color: '#71717a',
                      fontFamily: 'monospace',
                      whiteSpace: 'nowrap',
                      minWidth: 78,
                    }}
                  >
                    {formatUnix(l.created_at)}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#3f3f46',
                      fontWeight: 600,
                    }}
                  >
                    {l.account_name || '#' + l.account_id}
                  </span>
                  <Label
                    size='mini'
                    basic
                    color={
                      l.status === 'checked'
                        ? 'teal'
                        : l.status === 'already'
                        ? 'grey'
                        : 'red'
                    }
                    style={{ margin: 0 }}
                  >
                    {t('pool.checkin_status_' + l.status)}
                  </Label>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      minWidth: 44,
                      textAlign: 'right',
                      fontWeight: 700,
                      color: l.delta > 0 ? '#287d78' : '#a1a1aa',
                    }}
                  >
                    {l.delta != null
                      ? l.delta > 0
                        ? '+' + l.delta
                        : String(l.delta)
                      : t('pool.checkin_no_delta')}
                  </span>
                  <span
                    style={{
                      color: '#a1a1aa',
                      whiteSpace: 'nowrap',
                      minWidth: 50,
                      textAlign: 'right',
                    }}
                  >
                    {t('pool.checkin_mode_' + l.mode)}
                  </span>
                </div>
              ))
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  color: '#a1a1aa',
                  padding: '18px 0',
                  fontSize: 12,
                }}
              >
                {t('pool.checkin_empty')}
              </div>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setCheckinLogsOpen(false)}>
            {t('pool.checkin_close')}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Get Authorization Modal — workbuddy 登录流 / opencode 文件流 */}
      <Modal open={authOpen} onClose={closeAuthModal} size='small' closeIcon>
        <Modal.Header>{t('pool.auth_modal_title')}</Modal.Header>
        <Modal.Content>
          {/* 平台切换 */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <Button
              size='small'
              primary={authTab === 'workbuddy'}
              basic={authTab !== 'workbuddy'}
              onClick={() =>
                // 切 tab 保留流程状态：waiting 中/已取到凭据时切走再切回不丢，
                // 轮询也不因切 tab 中断（原先 reset 会清 authStatus/authState 导致停表）。
                setAuthTab('workbuddy')
              }
            >
              <Icon name='key' /> {t('pool.auth_tab_workbuddy')}
            </Button>
            <Button
              size='small'
              primary={authTab === 'trae'}
              basic={authTab !== 'trae'}
              onClick={() => setAuthTab('trae')}
            >
              <Icon name='globe' /> Trae
            </Button>
            <Button
              size='small'
              primary={authTab === 'catpaw'}
              basic={authTab !== 'catpaw'}
              onClick={() => setAuthTab('catpaw')}
            >
              <Icon name='paw' /> CatPaw
            </Button>
            <Button
              size='small'
              primary={authTab === 'qoder'}
              basic={authTab !== 'qoder'}
              onClick={() => setAuthTab('qoder')}
            >
              <Icon name='magic' /> Qoder
            </Button>
            <Button
              size='small'
              primary={authTab === 'opencode'}
              basic={authTab !== 'opencode'}
              onClick={() =>
                // 切 tab 保留流程状态（同上）。
                setAuthTab('opencode')
              }
            >
              <Icon name='code' /> {t('pool.auth_tab_opencode')}
            </Button>
          </div>

          {authTab === 'qoder' ? (
            <div>
              <p
                style={{
                  fontSize: 12,
                  color: '#71717a',
                  lineHeight: 1.7,
                  marginTop: 0,
                }}
              >
                Qoder 官方登录凭据加密存放在本机 CLI，无法由服务端直接截获，
                因此流程为「登录 → 本机提取 → 导入」三步。
              </p>
              <Button
                primary
                loading={qoderAuth.status === 'starting'}
                onClick={handleQoderStart}
              >
                <Icon name='linkify' /> 打开登录页并显示指引
              </Button>
              {qoderAuth.error ? (
                <p style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
                  {qoderAuth.error}
                </p>
              ) : null}
              {qoderAuth.status === 'ready' ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: '10px 12px',
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(24,24,27,0.08)',
                    fontSize: 12,
                    lineHeight: 1.9,
                    color: '#3f3f46',
                  }}
                >
                  {qoderAuth.guide.map((g, i) => (
                    <div key={i}>{g}</div>
                  ))}
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <Button
                      size='small'
                      primary
                      onClick={() => window.open(qoderAuth.loginUrl, '_blank')}
                    >
                      <Icon name='external alternate' />{' '}
                      {t('pool.auth_open_link')}
                    </Button>
                  </div>
                  <p
                    style={{
                      margin: '8px 0 0',
                      color: '#a1a1aa',
                      fontSize: 11,
                    }}
                  >
                    登录完成后运行 gen_qoder_import.py 生成导入文件，
                    回本页「导入 JSON」即可入池。
                  </p>
                </div>
              ) : null}
            </div>
          ) : authTab === 'catpaw' ? (
            <div>
              {(catpawAuth.status === 'idle' ||
                catpawAuth.status === 'starting') && (
                <div>
                  <p
                    style={{
                      fontSize: 12,
                      color: '#71717a',
                      lineHeight: 1.7,
                      marginTop: 0,
                    }}
                  >
                    1. 点击「获取授权链接」→ 2.
                    浏览器打开并用美团账号/手机验证码登录 → 3.
                    登录后本页自动检测并直接入池（无需粘贴任何链接）。 token 72
                    小时有效，过期后重新登录即可。
                  </p>
                  <Button
                    primary
                    loading={catpawAuth.status === 'starting'}
                    onClick={handleCatpawStart}
                  >
                    <Icon name='linkify' /> {t('pool.auth_start')}
                  </Button>
                </div>
              )}

              {catpawAuth.status === 'waiting' && catpawAuth.state && (
                <div>
                  <div
                    style={{
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(24,24,27,0.08)',
                      fontSize: 12,
                      wordBreak: 'break-all',
                      lineHeight: 1.6,
                      color: '#3f3f46',
                      maxHeight: 110,
                      overflow: 'auto',
                    }}
                  >
                    {catpawAuth.authUrl}
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <Button
                      size='small'
                      primary
                      onClick={() => window.open(catpawAuth.authUrl, '_blank')}
                    >
                      <Icon name='external alternate' />{' '}
                      {t('pool.auth_open_link')}
                    </Button>
                    <Button
                      size='small'
                      onClick={async () => {
                        const ok = await copyText(catpawAuth.authUrl);
                        if (ok) {
                          showSuccess(t('pool.auth_copied'));
                        } else {
                          showError(t('pool.auth_copy_failed'));
                        }
                      }}
                    >
                      <Icon name='copy' /> {t('pool.auth_copy_link')}
                    </Button>
                  </div>
                  <p
                    style={{
                      marginTop: 12,
                      fontSize: 12,
                      color: '#71717a',
                      marginBottom: 0,
                    }}
                  >
                    {t('pool.auth_waiting')}
                  </p>
                </div>
              )}

              {catpawAuth.status === 'done' && catpawAuth.result && (
                <Message positive>
                  <Message.Header>
                    <Icon name='check circle' /> {t('pool.auth_done')}
                  </Message.Header>
                  <p style={{ marginBottom: 0 }}>
                    {catpawAuth.result.nickname
                      ? `${t('pool.auth_nickname')}: ${
                          catpawAuth.result.nickname
                        }　`
                      : ''}
                    {catpawAuth.result.uid
                      ? `${t('pool.auth_uid')}: ${catpawAuth.result.uid}`
                      : ''}
                    <br />
                    账号已直接加入池。
                  </p>
                </Message>
              )}

              {catpawAuth.status === 'error' && (
                <div>
                  <Message negative>
                    <Message.Header>
                      <Icon name='warning circle' /> {t('pool.import_failed')}
                    </Message.Header>
                    {catpawAuth.error && (
                      <p style={{ marginBottom: 0 }}>{catpawAuth.error}</p>
                    )}
                  </Message>
                  <Button primary size='small' onClick={handleCatpawStart}>
                    <Icon name='refresh' /> {t('pool.auth_restart')}
                  </Button>
                </div>
              )}
            </div>
          ) : authTab === 'trae' ? (
            <div>
              {/* 区服选择 */}
              <div
                style={{
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 600, color: '#52525b' }}
                >
                  {t('pool.auth_realm')}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['cn', 'sg'].map((r) => (
                    <Button
                      key={r}
                      size='mini'
                      primary={traeAuth.realm === r}
                      basic={traeAuth.realm !== r}
                      disabled={
                        traeAuth.status === 'waiting' ||
                        traeAuth.status === 'starting'
                      }
                      onClick={() => {
                        setTraeAuth((p) => ({ ...p, realm: r }));
                        resetTraeAuth();
                      }}
                    >
                      {r === 'cn'
                        ? t('pool.auth_realm_cn')
                        : t('pool.auth_realm_global')}
                    </Button>
                  ))}
                </div>
              </div>

              {(traeAuth.status === 'idle' ||
                traeAuth.status === 'starting') && (
                <div>
                  <p
                    style={{
                      fontSize: 12,
                      color: '#71717a',
                      lineHeight: 1.7,
                      marginTop: 0,
                    }}
                  >
                    1. 点击「获取授权链接」→ 2. 浏览器打开并登录（手机号/验证码
                    一次）→ 3. 登录后浏览器跳到打不开的 127.0.0.1 地址 → 4.
                    复制地址栏完整链接粘贴回来，系统自动换 token 并入池。
                  </p>
                  <Button
                    primary
                    loading={traeAuth.status === 'starting'}
                    onClick={handleTraeStart}
                  >
                    <Icon name='linkify' /> {t('pool.auth_start')}
                  </Button>
                </div>
              )}

              {traeAuth.status === 'waiting' && traeAuth.state && (
                <div>
                  <div
                    style={{
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(24,24,27,0.08)',
                      fontSize: 12,
                      wordBreak: 'break-all',
                      lineHeight: 1.6,
                      color: '#3f3f46',
                      maxHeight: 110,
                      overflow: 'auto',
                    }}
                  >
                    {traeAuth.authUrl}
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <Button
                      size='small'
                      primary
                      onClick={() => window.open(traeAuth.authUrl, '_blank')}
                    >
                      <Icon name='external alternate' />{' '}
                      {t('pool.auth_open_link')}
                    </Button>
                    <Button
                      size='small'
                      onClick={async () => {
                        const ok = await copyText(traeAuth.authUrl);
                        if (ok) {
                          showSuccess(t('pool.auth_copied'));
                        } else {
                          showError(t('pool.auth_copy_failed'));
                        }
                      }}
                    >
                      <Icon name='copy' /> {t('pool.auth_copy_link')}
                    </Button>
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#52525b',
                        marginBottom: 6,
                      }}
                    >
                      登录完成后，粘贴浏览器地址栏的完整回调链接（127.0.0.1
                      打不开是正常的）：
                    </div>
                    <Form.Input
                      size='small'
                      placeholder='http://127.0.0.1:18080/authorize?refreshToken=...'
                      value={traeAuth.callbackUrl}
                      onChange={(e, { value }) =>
                        setTraeAuth((p) => ({ ...p, callbackUrl: value }))
                      }
                    />
                    <div style={{ marginTop: 10 }}>
                      <Button
                        primary
                        size='small'
                        disabled={!traeAuth.callbackUrl.trim()}
                        loading={traeAuth.status === 'starting'}
                        onClick={handleTraeComplete}
                      >
                        <Icon name='sign-in alternate' /> 完成登录并入池
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {traeAuth.status === 'done' && traeAuth.result && (
                <Message positive>
                  <Message.Header>
                    <Icon name='check circle' /> {t('pool.auth_done')}
                  </Message.Header>
                  <p style={{ marginBottom: 0 }}>
                    {traeAuth.result.nickname
                      ? `${t('pool.auth_nickname')}: ${
                          traeAuth.result.nickname
                        }　`
                      : ''}
                    {traeAuth.result.uid
                      ? `${t('pool.auth_uid')}: ${traeAuth.result.uid}`
                      : ''}
                    <br />
                    账号已直接加入池（realm: {traeAuth.result.realm}）。
                  </p>
                </Message>
              )}

              {traeAuth.status === 'error' && (
                <div>
                  <Message negative>
                    <Message.Header>
                      <Icon name='warning circle' /> {t('pool.import_failed')}
                    </Message.Header>
                    {traeAuth.error && (
                      <p style={{ marginBottom: 0 }}>{traeAuth.error}</p>
                    )}
                  </Message>
                  <Button primary size='small' onClick={handleTraeStart}>
                    <Icon name='refresh' /> {t('pool.auth_restart')}
                  </Button>
                </div>
              )}
            </div>
          ) : authTab === 'workbuddy' ? (
            <div>
              {/* 区服选择 */}
              <div
                style={{
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 600, color: '#52525b' }}
                >
                  {t('pool.auth_realm')}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['global', 'cn'].map((r) => (
                    <Button
                      key={r}
                      size='mini'
                      primary={authRealm === r}
                      basic={authRealm !== r}
                      disabled={
                        authStatus === 'waiting' || authStatus === 'starting'
                      }
                      onClick={() => {
                        setAuthRealm(r);
                        resetAuthFlow();
                      }}
                    >
                      {t(`pool.auth_realm_${r}`)}
                    </Button>
                  ))}
                </div>
              </div>

              {(authStatus === 'idle' || authStatus === 'starting') && (
                <div>
                  <p
                    style={{
                      fontSize: 12,
                      color: '#71717a',
                      lineHeight: 1.7,
                      marginTop: 0,
                    }}
                  >
                    {t('pool.auth_step_tip')}
                  </p>
                  <Button
                    primary
                    loading={authStatus === 'starting'}
                    onClick={handleAuthStart}
                  >
                    <Icon name='linkify' /> {t('pool.auth_start')}
                  </Button>
                </div>
              )}

              {authStatus === 'waiting' && authState && (
                <div>
                  <div
                    style={{
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(24,24,27,0.08)',
                      fontSize: 12,
                      wordBreak: 'break-all',
                      lineHeight: 1.6,
                      color: '#3f3f46',
                      maxHeight: 110,
                      overflow: 'auto',
                    }}
                  >
                    {authState.auth_url}
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <Button
                      size='small'
                      primary
                      onClick={() => window.open(authState.auth_url, '_blank')}
                    >
                      <Icon name='external alternate' />{' '}
                      {t('pool.auth_open_link')}
                    </Button>
                    <Button
                      size='small'
                      onClick={async () => {
                        const ok = await copyText(authState.auth_url);
                        if (ok) {
                          setAuthCopied(true);
                          showSuccess(t('pool.auth_copied'));
                          setTimeout(() => setAuthCopied(false), 2000);
                        } else {
                          showError(t('pool.auth_copy_failed'));
                        }
                      }}
                    >
                      <Icon name={authCopied ? 'check' : 'copy'} />{' '}
                      {authCopied
                        ? t('pool.auth_copied')
                        : t('pool.auth_copy_link')}
                    </Button>
                  </div>
                  <p
                    style={{
                      marginTop: 12,
                      marginBottom: 0,
                      fontSize: 12,
                      color: '#71717a',
                    }}
                  >
                    <Loader active inline size='mini' />{' '}
                    {t('pool.auth_waiting')}
                  </p>
                </div>
              )}

              {authStatus === 'done' && authCred && (
                <div>
                  <Message positive>
                    <Message.Header>
                      <Icon name='check circle' /> {t('pool.auth_done')}
                    </Message.Header>
                    <p style={{ marginBottom: 0 }}>
                      {t('pool.auth_done_desc')}
                      {authCred?.account?.nickname || authCred?.account?.uid ? (
                        <>
                          <br />
                          {authCred?.account?.nickname
                            ? `${t('pool.auth_nickname')}: ${
                                authCred.account.nickname
                              }`
                            : ''}
                          {authCred?.account?.nickname && authCred?.account?.uid
                            ? '　'
                            : ''}
                          {authCred?.account?.uid
                            ? `${t('pool.auth_uid')}: ${authCred.account.uid}`
                            : ''}
                        </>
                      ) : null}
                    </p>
                  </Message>
                  <div
                    style={{
                      marginBottom: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#52525b',
                    }}
                  >
                    {t('pool.auth_preview')}
                  </div>
                  <pre style={authPreviewStyle}>
                    {JSON.stringify(authCred, null, 2)}
                  </pre>
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <Button
                      primary
                      size='small'
                      onClick={() => downloadJsonFile(authFilename, authCred)}
                    >
                      <Icon name='download' /> {t('pool.auth_export')}
                    </Button>
                    <Button
                      size='small'
                      color='teal'
                      onClick={() => handleAuthImport(authCred)}
                    >
                      <Icon name='sign-in alternate' /> {t('pool.auth_import')}
                    </Button>
                  </div>
                </div>
              )}

              {(authStatus === 'expired' || authStatus === 'error') && (
                <div>
                  <Message negative>
                    <Message.Header>
                      <Icon name='warning circle' />{' '}
                      {authStatus === 'expired'
                        ? t('pool.auth_expired')
                        : t('pool.import_failed')}
                    </Message.Header>
                    {authError && (
                      <p style={{ marginBottom: 0 }}>{authError}</p>
                    )}
                  </Message>
                  <Button primary size='small' onClick={handleAuthStart}>
                    <Icon name='refresh' /> {t('pool.auth_restart')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p
                style={{
                  fontSize: 12,
                  color: '#71717a',
                  lineHeight: 1.7,
                  marginTop: 0,
                }}
              >
                {t('pool.auth_oc_tip')}
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div>
                  <label style={ocLabelStyle}>{t('pool.auth_name')}</label>
                  <input
                    style={ocInputStyle}
                    value={ocForm.name}
                    onChange={(e) =>
                      setOcForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label style={ocLabelStyle}>{t('pool.auth_endpoint')}</label>
                  <input
                    style={ocInputStyle}
                    placeholder='https://opencode.ai/zen/v1'
                    value={ocForm.endpoint}
                    onChange={(e) =>
                      setOcForm((f) => ({ ...f, endpoint: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label style={ocLabelStyle}>
                    {t('pool.auth_concurrency')}
                  </label>
                  <input
                    style={ocInputStyle}
                    type='number'
                    min='1'
                    value={ocForm.concurrency}
                    onChange={(e) =>
                      setOcForm((f) => ({ ...f, concurrency: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label style={ocLabelStyle}>{t('pool.auth_priority')}</label>
                  <input
                    style={ocInputStyle}
                    type='number'
                    value={ocForm.priority}
                    onChange={(e) =>
                      setOcForm((f) => ({ ...f, priority: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label style={ocLabelStyle}>
                    {t('pool.auth_window_limit')}
                  </label>
                  <input
                    style={ocInputStyle}
                    type='number'
                    min='1'
                    value={ocForm.window_limit}
                    onChange={(e) =>
                      setOcForm((f) => ({ ...f, window_limit: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div
                style={{
                  marginBottom: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#52525b',
                }}
              >
                {t('pool.auth_preview')}
              </div>
              <pre style={authPreviewStyle}>
                {JSON.stringify(ocPayload, null, 2)}
              </pre>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <Button
                  primary
                  size='small'
                  onClick={() =>
                    downloadJsonFile('opencode-import.json', ocPayload)
                  }
                >
                  <Icon name='download' /> {t('pool.auth_export')}
                </Button>
                <Button
                  size='small'
                  color='teal'
                  onClick={() => handleAuthImport(ocPayload)}
                >
                  <Icon name='sign-in alternate' /> {t('pool.auth_import')}
                </Button>
              </div>
            </div>
          )}

          {/* 一键导入结果（两种流共用） */}
          {authImportMsg && (
            <Message
              positive={authImportMsg.success}
              negative={!authImportMsg.success}
              style={{ marginTop: 14 }}
            >
              <Message.Header>
                {authImportMsg.success
                  ? t('pool.import_success')
                  : t('pool.import_failed')}
              </Message.Header>
              <p style={{ marginBottom: 0 }}>
                {authImportMsg.success
                  ? t('pool.auth_imported', { count: authImportMsg.count })
                  : authImportMsg.error}
              </p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeAuthModal}>{t('pool.close')}</Button>
        </Modal.Actions>
      </Modal>

      {/* Account list */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Loader active size='large' />
        </div>
      ) : accounts.length === 0 ? (
        <div className='studio-empty-state'>
          <div className='studio-empty-icon'>
            <Icon name='database' />
          </div>
          <strong>{t('pool.empty_title')}</strong>
          <span>{t('pool.empty_desc')}</span>
          <div className='studio-empty-features'>
            <span>
              <Icon name='lock' />
              {t('pool.empty_feature_credentials')}
            </span>
            <span>
              <Icon name='chart area' />
              {t('pool.empty_feature_windows')}
            </span>
            <span>
              <Icon name='globe' />
              {t('pool.empty_feature_proxy')}
            </span>
          </div>
          <div className='studio-empty-actions'>
            <Button primary onClick={() => setImportOpen(true)}>
              <Icon name='upload' />
              {t('pool.import')}
            </Button>
          </div>
          <small className='studio-empty-note'>{t('pool.empty_hint')}</small>
        </div>
      ) : (
        <>
          <div
            className='studio-management-toolbar'
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 18px',
              borderRadius: '15px',
              marginBottom: 14,
              // 卡片带 backdrop-filter 会创建堆叠上下文，后绘制的卡片会盖住
              // 工具条里的 Dropdown 浮层菜单 —— 抬一层 z-index 修掉
              position: 'relative',
              zIndex: 20,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
              {t('pool.account_list')}{' '}
              <span style={{ color: '#a1a1aa', fontWeight: 400 }}>
                ({filteredAccounts.length}
                {platformFilter ? `/${accounts.length}` : ''})
              </span>
            </h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* 平台筛选：动态聚合当前账号平台，样式走 mini 紧凑与工具条一致 */}
              <Dropdown
                selection
                compact
                size='mini'
                value={platformFilter}
                options={platformOptions}
                onChange={(e, { value }) => setPlatformFilter(value)}
                style={{ minWidth: 130 }}
              />
              {/* 一键测试：并发触发当前筛选下全部账号的单测；进行中变「停止」+进度 */}
              <Button
                size='mini'
                color={bulkTesting ? 'orange' : 'blue'}
                basic
                onClick={handleTestAll}
                disabled={!bulkTesting && filteredAccounts.length === 0}
                icon={bulkTesting ? 'stop' : 'bolt'}
                content={
                  bulkTesting
                    ? `${t('pool.test_all_stop')} (${bulkProgress.done}/${
                        bulkProgress.total
                      })`
                    : t('pool.test_all')
                }
              />
              {/* 只在确实有 WorkBuddy 账号时展示：签到轮会跳过其它平台账号，
                  否则 Codex-only 的池点了空跑却提示"已触发" */}
              {hasWorkbuddy && (
                <>
                  <Button
                    size='mini'
                    basic
                    color='teal'
                    loading={checkingIn}
                    disabled={checkingIn}
                    onClick={handleCheckin}
                  >
                    <Icon name='calendar check' /> {t('pool.checkin')}
                  </Button>
                  {hasTraeCn && (
                    <Button
                      size='mini'
                      basic
                      color='teal'
                      loading={traeCheckingIn}
                      disabled={traeCheckingIn}
                      onClick={handleTraeCheckin}
                    >
                      <Icon name='calendar check' /> {t('pool.trae_checkin')}
                    </Button>
                  )}
                  <Button size='mini' basic onClick={openCheckinLogs}>
                    <Icon name='history' /> {t('pool.checkin_records')}
                  </Button>
                </>
              )}
              <Button size='mini' color='red' basic onClick={handleClear}>
                <Icon name='trash alternate outline' /> {t('pool.clear_all')}
              </Button>
            </div>
          </div>

          <div
            className='pool-list'
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
          >
            {groupedAccounts.map(([grp, list]) => (
              <React.Fragment key={grp}>
                {/* ── 分组头 ── */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '6px 2px 0 2px',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#52525b',
                    }}
                  >
                    {platformLabels[grp] || grp}
                  </span>
                  <span
                    style={{
                      fontSize: 10.5,
                      color: '#a1a1aa',
                      background: 'rgba(24,24,27,0.05)',
                      padding: '1px 8px',
                      borderRadius: 999,
                    }}
                  >
                    {list.length}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: 1,
                      background: 'rgba(24,24,27,0.07)',
                    }}
                  />
                </div>
                {list.map((acc) => {
                  const sc = statusColors[acc.status] || statusColors.active;
                  const proxyValue =
                    acc.proxy_subscription_id > 0
                      ? `${acc.proxy_subscription_id}-${acc.proxy_node_id}`
                      : '0-0';
                  const hasProxy = acc.proxy_subscription_id > 0;
                  const hasCodex =
                    acc.codex_5h_used > 0 ||
                    acc.codex_5h_reset > 0 ||
                    acc.codex_7d_used > 0 ||
                    acc.codex_7d_reset > 0;
                  const expanded = expandedId === acc.id;
                  return (
                    <div
                      key={acc.id}
                      style={{
                        border: `1px solid ${
                          expanded ? sc.border : 'rgba(24,24,27,0.07)'
                        }`,
                        borderLeft: `3px solid ${sc.text}`,
                        borderRadius: '12px',
                        background: expanded ? sc.bg : 'rgba(255,255,255,0.72)',
                        boxShadow: expanded
                          ? '0 1px 3px rgba(24,24,27,0.04), 0 8px 24px rgba(24,24,27,0.03)'
                          : 'none',
                        backdropFilter: 'blur(12px)',
                        position: 'relative',
                        transition: 'box-shadow 0.2s, background 0.2s',
                      }}
                    >
                      {/* ── 摘要行（收起态即整卡；点击切换展开详情） ── */}
                      <div
                        onClick={() => setExpandedId(expanded ? '' : acc.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '9px 12px',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        <Icon
                          name={expanded ? 'angle down' : 'angle right'}
                          style={{ margin: 0, color: '#a1a1aa', flexShrink: 0 }}
                        />
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#18181b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: '0 1 auto',
                            maxWidth: '40%',
                          }}
                        >
                          {acc.name || acc.email || t('pool.unnamed')}
                        </div>
                        <span
                          style={{
                            fontSize: 10.5,
                            color: '#71717a',
                            flexShrink: 0,
                            background: 'rgba(24,24,27,0.045)',
                            padding: '2px 8px',
                            borderRadius: 999,
                          }}
                        >
                          {[
                            platformLabels[acc.platform] || acc.platform,
                            acc.plan_type,
                          ]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </span>
                        {acc.note ? (
                          <span
                            style={{
                              fontSize: 10.5,
                              color: '#287d78',
                              flexShrink: 0,
                              maxWidth: '18%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            📝 {acc.note}
                          </span>
                        ) : null}
                        {/* 摘要行中段：代理位置 / 冷却剩余（有则显） */}
                        <span
                          style={{
                            fontSize: 10.5,
                            color: hasProxy ? '#287d78' : '#a1a1aa',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {hasProxy
                            ? `${acc.proxy_location || '...'}`
                            : acc.platform !== 'workbuddy' &&
                              acc.status === 'rate_limited' &&
                              acc.rate_limited_until
                            ? `⏳ ${formatTime(acc.rate_limited_until)}`
                            : t('pool.direct')}
                        </span>
                        {/* 摘要行内嵌快捷操作：测活 / 暂停（阻止冒泡） */}
                        <Icon
                          name='bolt'
                          title={t('pool.test')}
                          style={{
                            margin: 0,
                            cursor: 'pointer',
                            color: testing === acc.id ? '#287d78' : '#a1a1aa',
                            padding: '4px 6px',
                            borderRadius: 8,
                            background:
                              testing === acc.id
                                ? 'rgba(40,125,120,0.1)'
                                : 'transparent',
                            flexShrink: 0,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTest(acc.id);
                          }}
                        />
                        <Icon
                          name={acc.paused ? 'play' : 'pause'}
                          title={
                            acc.paused ? t('pool.resume') : t('pool.pause')
                          }
                          style={{
                            margin: 0,
                            cursor: 'pointer',
                            color:
                              pausing === acc.id
                                ? '#287d78'
                                : acc.paused
                                ? '#287d78'
                                : '#c8821e',
                            padding: '4px 6px',
                            borderRadius: 8,
                            background:
                              pausing === acc.id
                                ? 'rgba(40,125,120,0.1)'
                                : 'transparent',
                            flexShrink: 0,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePause(acc);
                          }}
                        />
                        <Label
                          color={sc.label}
                          size='mini'
                          circular
                          style={{ margin: 0, flexShrink: 0 }}
                        >
                          {t(`pool.${acc.status}`, {
                            defaultValue: acc.status,
                          })}
                        </Label>
                        {acc.paused ? (
                          <Label
                            color='black'
                            size='mini'
                            circular
                            style={{ margin: 0, flexShrink: 0, opacity: 0.75 }}
                          >
                            <Icon
                              name='pause'
                              style={{ margin: '0 3px 0 0' }}
                            />
                            {t('pool.paused')}
                          </Label>
                        ) : null}
                      </div>

                      {/* ── 详情区（仅展开时渲染） ── */}
                      {expanded ? (
                        <div style={{ padding: '2px 14px 12px 26px' }}>
                          {/* ── 备注（点击铅笔编辑，失焦保存） ── */}
                          <NoteEditor
                            accId={acc.id}
                            value={acc.note || ''}
                            onSaved={(note) =>
                              setAccounts((prev) =>
                                prev.map((a) =>
                                  a.id === acc.id ? { ...a, note } : a
                                )
                              )
                            }
                          />
                          {/* ── 优先级（数字越大越先被调度；高优先级限流/挂掉后自动降级） ── */}
                          <PriorityEditor
                            accId={acc.id}
                            value={acc.priority ?? 0}
                            onSaved={(priority) =>
                              setAccounts((prev) =>
                                prev.map((a) =>
                                  a.id === acc.id ? { ...a, priority } : a
                                )
                              )
                            }
                          />
                          {/* Proxy selector — 紧凑行式布局 */}
                          <div
                            className='pool-proxy-row'
                            style={{
                              marginBottom: 7,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '4px 8px',
                              borderRadius: 10,
                              background: 'rgba(255,255,255,0.5)',
                              border: '1px solid rgba(24,24,27,0.04)',
                            }}
                          >
                            <Icon
                              name='globe'
                              size='small'
                              style={{
                                color: '#71717a',
                                margin: 0,
                                flexShrink: 0,
                              }}
                            />
                            <Dropdown
                              selection
                              compact
                              search
                              size='mini'
                              value={proxyValue}
                              options={proxyOptions}
                              loading={proxyLoading[acc.id]}
                              onChange={(e, { value }) =>
                                handleProxyChange(acc.id, value)
                              }
                              style={{
                                minWidth: 0,
                                flex: 1,
                                fontSize: 12,
                                // 扁平控件（透明底/无描边）。注意：这里不能用 style 直接生效——
                                // .studio-page .ui.selection.dropdown 的 !important 会盖掉
                                // 行内样式（行内非 important 打不过 author !important），
                                // 所以真正的覆盖写在 Settings.css 的 .studio-page .pool-proxy-row
                                // 规则里（作用域更深一档）。这三行保留只是为了表达意图。
                                border: 'none',
                                boxShadow: 'none',
                                background: 'transparent',
                              }}
                            />
                            {/* IP badge */}
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                flexShrink: 0,
                              }}
                            >
                              {hasProxy ? (
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    color: '#287d78',
                                    padding: '2px 7px',
                                    borderRadius: 999,
                                    background: 'rgba(40,125,120,0.1)',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {acc.proxy_location || '...'}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    color: '#71717a',
                                    padding: '2px 7px',
                                    borderRadius: 999,
                                    background: 'rgba(0,0,0,0.04)',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {status.location || t('pool.direct')}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Info rows */}
                          <div
                            style={{
                              fontSize: 10.5,
                              lineHeight: 1.55,
                              color: '#52525b',
                              marginBottom: 7,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <span style={{ color: '#a1a1aa' }}>
                                {t('pool.token_expires')}
                              </span>
                              <span
                                style={{
                                  color:
                                    acc.status === 'expired'
                                      ? '#b83d3d'
                                      : '#287d78',
                                  fontFamily: 'monospace',
                                  fontSize: 11,
                                }}
                              >
                                {acc.expires_at
                                  ? formatTime(acc.expires_at)
                                  : '--'}
                              </span>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <span style={{ color: '#a1a1aa' }}>
                                {t('pool.last_used')}
                              </span>
                              <span
                                style={{
                                  color: '#71717a',
                                  fontFamily: 'monospace',
                                  fontSize: 11,
                                }}
                              >
                                {formatTime(acc.last_used_at)}
                              </span>
                            </div>
                          </div>

                          {/* 可拉取模型 — 展开式清单（显示该卡片能服务的模型） */}
                          <div
                            style={{
                              marginBottom: 7,
                              padding: '8px 12px',
                              borderRadius: 10,
                              background: 'rgba(255,255,255,0.6)',
                              border: '1px solid rgba(24,24,27,0.04)',
                            }}
                          >
                            <div
                              onClick={() => toggleModels(acc)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                cursor: 'pointer',
                                userSelect: 'none',
                              }}
                            >
                              <Icon
                                name='boxes'
                                size='small'
                                style={{ color: '#71717a', margin: 0 }}
                              />
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: '#71717a',
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  flex: 1,
                                }}
                              >
                                {t('pool.models_title')}
                              </span>
                              <Icon
                                name={
                                  modelsOpen[acc.id] ? 'angle up' : 'angle down'
                                }
                                size='small'
                                style={{ color: '#a1a1aa', margin: 0 }}
                              />
                            </div>
                            {modelsOpen[acc.id] ? (
                              <div style={{ marginTop: 8 }}>
                                {accModels[acc.id]?.loading ? (
                                  <span
                                    style={{ fontSize: 11, color: '#a1a1aa' }}
                                  >
                                    {t('pool.models_loading')}
                                  </span>
                                ) : accModels[acc.id]?.models?.length > 0 ? (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: 4,
                                    }}
                                  >
                                    {accModels[acc.id].models.map((m) => (
                                      <span
                                        key={m}
                                        style={{
                                          fontSize: 10,
                                          fontFamily: 'monospace',
                                          color: '#3f3f46',
                                          padding: '2px 7px',
                                          borderRadius: 999,
                                          background: 'rgba(24,24,27,0.05)',
                                          border:
                                            '1px solid rgba(24,24,27,0.06)',
                                        }}
                                      >
                                        {m}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span
                                    style={{ fontSize: 11, color: '#a1a1aa' }}
                                  >
                                    {accModels[acc.id]?.error ||
                                      t('pool.models_empty')}
                                  </span>
                                )}
                              </div>
                            ) : null}
                          </div>

                          {/* 积分/签到/冷却信息卡 — workbuddy + trae CN 平台账号 */}
                          {acc.platform === 'workbuddy' ||
                          (acc.platform === 'trae' &&
                            !['sg', 'global'].includes(
                              (acc.realm || '').toLowerCase()
                            )) ? (
                            <div
                              style={{
                                marginBottom: 10,
                                padding: '10px 12px',
                                borderRadius: 10,
                                background: 'rgba(255,255,255,0.6)',
                                border: '1px solid rgba(24,24,27,0.04)',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  marginBottom: 8,
                                }}
                              >
                                <Icon
                                  name='gift'
                                  size='small'
                                  style={{ color: '#71717a', margin: 0 }}
                                />
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: '#71717a',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  {acc.platform === 'trae'
                                    ? 'Trae'
                                    : 'WorkBuddy'}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  marginBottom: 2,
                                }}
                              >
                                <span
                                  style={{ fontSize: 11, color: '#a1a1aa' }}
                                >
                                  {t('pool.credits_remain')}
                                </span>
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#18181b',
                                    fontFamily: 'monospace',
                                  }}
                                >
                                  {(acc.credits_remain || 0).toLocaleString()}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <span
                                  style={{ fontSize: 11, color: '#a1a1aa' }}
                                >
                                  {t('pool.last_checkin')}
                                </span>
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: '#52525b',
                                    fontFamily: 'monospace',
                                  }}
                                >
                                  {formatUnix(acc.last_checkin_at)}
                                </span>
                              </div>
                              {acc.rate_limited_until &&
                              new Date(acc.rate_limited_until).getTime() >
                                Date.now() ? (
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginTop: 2,
                                  }}
                                >
                                  <span
                                    style={{ fontSize: 11, color: '#a1a1aa' }}
                                  >
                                    {t('pool.cool_until')}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      color:
                                        acc.status === 'rate_limited'
                                          ? '#b83d3d'
                                          : '#b45309',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {formatTime(acc.rate_limited_until)}
                                  </span>
                                </div>
                              ) : null}
                              {/* 单账号签到 — workbuddy 平台独立入口（不等工具条一键轮） */}
                              <div
                                style={{
                                  marginTop: 8,
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Button
                                  size='mini'
                                  basic
                                  color='teal'
                                  loading={accChecking === acc.id}
                                  disabled={
                                    accChecking === acc.id ||
                                    acc.status === 'disabled'
                                  }
                                  onClick={() => handleAccountCheckin(acc)}
                                >
                                  <Icon name='calendar check' />{' '}
                                  {t('pool.checkin_single')}
                                </Button>
                                {accCheckinMsg &&
                                accCheckinMsg.id === acc.id ? (
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 11,
                                      color: accCheckinMsg.ok
                                        ? '#287d78'
                                        : '#b83d3d',
                                    }}
                                  >
                                    {accCheckinMsg.text}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          ) : null}

                          {/* 限流冷却 — 非 WorkBuddy 平台（wb 账号的显示在上方积分卡内） */}
                          {acc.platform !== 'workbuddy' &&
                          acc.status === 'rate_limited' &&
                          acc.rate_limited_until ? (
                            <div
                              style={{
                                marginBottom: 10,
                                padding: '8px 12px',
                                borderRadius: 10,
                                background: 'rgba(255,255,255,0.6)',
                                border: '1px solid rgba(24,24,27,0.04)',
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <span style={{ fontSize: 11, color: '#a1a1aa' }}>
                                {t('pool.cool_until')}
                              </span>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: '#b83d3d',
                                  fontFamily: 'monospace',
                                }}
                              >
                                {formatTime(acc.rate_limited_until)}
                              </span>
                            </div>
                          ) : null}

                          {/* Codex 窗口使用情况 — 带动效的进度条 */}
                          {hasCodex ? (
                            <div
                              style={{
                                marginBottom: 10,
                                padding: '10px 12px',
                                borderRadius: 10,
                                background: 'rgba(255,255,255,0.6)',
                                border: '1px solid rgba(24,24,27,0.04)',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  marginBottom: 8,
                                }}
                              >
                                <Icon
                                  name='chart bar'
                                  size='small'
                                  style={{ color: '#71717a', margin: 0 }}
                                />
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: '#71717a',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  Usage Windows
                                </span>
                              </div>
                              {/* 5h 窗口 — 始终显示（即使 0%） */}
                              <AnimatedBar
                                percent={acc.codex_5h_used || 0}
                                label={t('pool.window_5h')}
                                resetText={
                                  acc.codex_5h_reset > 0
                                    ? t('pool.reset_in', {
                                        time: formatReset(acc.codex_5h_reset),
                                      })
                                    : ''
                                }
                                color='#22c55e'
                              />
                              {/* 7d 窗口 */}
                              <AnimatedBar
                                percent={acc.codex_7d_used || 0}
                                label={t('pool.window_7d')}
                                resetText={
                                  acc.codex_7d_reset > 0
                                    ? t('pool.reset_in', {
                                        time: formatReset(acc.codex_7d_reset),
                                      })
                                    : ''
                                }
                                color='#3b82f6'
                              />
                            </div>
                          ) : null}

                          {/* Token 用量统计 */}
                          {hasCodex &&
                          (acc.window_request_count > 0 ||
                            acc.window_total_tokens > 0) ? (
                            <div
                              style={{
                                marginBottom: 10,
                                padding: '10px 12px',
                                borderRadius: 10,
                                background: 'rgba(255,255,255,0.6)',
                                border: '1px solid rgba(24,24,27,0.04)',
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  marginBottom: 8,
                                }}
                              >
                                <Icon
                                  name='database'
                                  size='small'
                                  style={{ color: '#71717a', margin: 0 }}
                                />
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: '#71717a',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  Token Usage (This Window)
                                </span>
                              </div>
                              <div
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 1fr',
                                  gap: '6px 12px',
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <span
                                    style={{ fontSize: 11, color: '#a1a1aa' }}
                                  >
                                    {t('pool.request_count')}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: '#18181b',
                                    }}
                                  >
                                    {acc.window_request_count || 0}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <span
                                    style={{ fontSize: 11, color: '#a1a1aa' }}
                                  >
                                    {t('pool.total_tokens')}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: '#18181b',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {(
                                      acc.window_total_tokens || 0
                                    ).toLocaleString()}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <span
                                    style={{ fontSize: 11, color: '#a1a1aa' }}
                                  >
                                    {t('pool.input_tokens')}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: '#52525b',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {(
                                      acc.window_input_tokens || 0
                                    ).toLocaleString()}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <span
                                    style={{ fontSize: 11, color: '#a1a1aa' }}
                                  >
                                    {t('pool.output_tokens')}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: '#52525b',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {(
                                      acc.window_output_tokens || 0
                                    ).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : null}

                          {acc.error_message && (
                            <div
                              style={{
                                marginBottom: 10,
                                padding: '6px 10px',
                                borderRadius: 8,
                                background: 'rgba(190,65,65,0.06)',
                                fontSize: 10,
                                color: '#b83d3d',
                                wordBreak: 'break-all',
                                lineHeight: 1.5,
                              }}
                            >
                              <Icon
                                name='warning circle'
                                size='small'
                                style={{ margin: '0 4px 0 0' }}
                              />
                              {acc.error_message.slice(0, 120)}
                            </div>
                          )}

                          {/* Actions — 详情区内：导出 / 删除（测活/暂停已在摘要行） */}
                          <div
                            style={{
                              display: 'flex',
                              gap: 5,
                              paddingTop: 6,
                              marginTop: 2,
                              borderTop: '1px solid rgba(24,24,27,0.06)',
                            }}
                          >
                            <Icon
                              name='download'
                              title={t('pool.export')}
                              style={{
                                margin: 0,
                                cursor: 'pointer',
                                color:
                                  exporting === acc.id ? '#287d78' : '#52525b',
                                padding: '6px 8px',
                                borderRadius: 8,
                                background:
                                  exporting === acc.id
                                    ? 'rgba(40,125,120,0.1)'
                                    : 'transparent',
                              }}
                              onClick={() => handleAccountExport(acc)}
                            />
                            <div style={{ flex: 1 }} />
                            <Icon
                              name='trash'
                              title={t('pool.confirm_delete')}
                              style={{
                                margin: 0,
                                cursor: 'pointer',
                                color: '#b83d3d',
                                padding: '6px 8px',
                                borderRadius: 8,
                              }}
                              onClick={() => handleDelete(acc.id)}
                            />
                          </div>

                          {/* Test result */}
                          {testResult[acc.id] && (
                            <div
                              style={{
                                marginTop: 8,
                                padding: '8px 10px',
                                borderRadius: 8,
                                background: 'rgba(255,255,255,0.6)',
                                border: '1px solid rgba(24,24,27,0.04)',
                                fontSize: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 8,
                                  alignItems: 'center',
                                }}
                              >
                                <span
                                  style={{
                                    color: testResult[acc.id].success
                                      ? testResult[acc.id].status_code === 429
                                        ? '#b7791f'
                                        : '#287d78'
                                      : '#b83d3d',
                                    fontWeight: 700,
                                  }}
                                >
                                  {testResult[acc.id].success ? '✓' : '✕'}
                                </span>
                                <span style={{ color: '#52525b' }}>
                                  HTTP {testResult[acc.id].status_code || 'N/A'}
                                </span>
                                <span style={{ color: '#a1a1aa' }}>
                                  {testResult[acc.id].duration_ms || 0}ms
                                </span>
                              </div>
                              {(testResult[acc.id].error ||
                                (testResult[acc.id].status_code >= 400 &&
                                  testResult[acc.id].body)) && (
                                <pre
                                  style={{
                                    color: testResult[acc.id].success
                                      ? '#b7791f'
                                      : '#71717a',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    maxHeight: 50,
                                    overflow: 'auto',
                                    margin: '4px 0 0 0',
                                    fontSize: 10,
                                  }}
                                >
                                  {testResult[acc.id].error ||
                                    testResult[acc.id].body}
                                </pre>
                              )}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </StudioPage>
  );
};

export default Pool;
